// Confirmation d'upload résistante au passage en arrière-plan.
//
// Sept. 2026 (KAIYLO-FRONTEND-57) : le fichier arrive à 100 %, mais l'élève a
// quitté l'app ; le PATCH de confirmation part page masquée, iOS coupe le
// réseau → « Network Error », sans nouvel essai. La vidéo finissait READY
// grâce au balayage backend (~50 min plus tard), mais l'élève voyait une erreur.
// On attend donc que la page soit visible, puis on réessaie (PATCH idempotent).
//
// Oct. 2026 (KAIYLO-FRONTEND-55) : même « Network Error » sur iPhone au POST
// /videos qui crée le placeholder, juste après le choix de la vidéo. La ligne
// était souvent créée côté serveur (réponse perdue). Ce POST n'est rejouable
// que si le serveur peut retrouver la ligne de la série : voir canRetryPlaceholder.

export function waitUntilVisible(doc = typeof document !== 'undefined' ? document : undefined) {
  if (!doc || doc.visibilityState !== 'hidden') return Promise.resolve();
  return new Promise((resolve) => {
    const onChange = () => {
      if (doc.visibilityState === 'hidden') return;
      doc.removeEventListener('visibilitychange', onChange);
      resolve();
    };
    doc.addEventListener('visibilitychange', onChange);
  });
}

// Réseau coupé (pas de réponse), 5xx, jeton expiré (401 : getAuthToken
// rafraîchit un jeton proche de l'expiration (cas d'une page restée masquée
// longtemps) ; sinon le réessai est inutile mais sans danger), timeout (408)
// ou limite (429). Pas les autres 4xx.
export function isRetryableConfirmError(err) {
  const status = err?.response?.status;
  if (!status) return true;
  return status >= 500 || status === 401 || status === 408 || status === 429;
}

// POST /videos réutilise la ligne déjà créée pour la même séance + exercice +
// série (createVideoPlaceholderCore côté Backend) : le rejouer ne crée pas de
// doublon. Sans exercise_id (exercice hors bibliothèque) ou sans set_index, le
// serveur insère une nouvelle ligne à chaque appel : un réessai après une
// requête arrivée malgré l'erreur laisserait une ligne orpheline, qui passe en
// FAILED et s'affiche quand même dans la vidéothèque du coach.
export function canRetryPlaceholder(tusMetadata) {
  return Boolean(
    tusMetadata?.assigned_session_id &&
    tusMetadata?.exercise_id &&
    tusMetadata?.metadata?.set_index != null
  );
}

const defaultSleep =(ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Oct. 2026 (KAIYLO-FRONTEND-5Z) : retour vidéo coach de 49 Mo (Safari Mac),
// « Network Error » au PATCH de confirmation, alors que la ligne
// coach_feedback_videos est créée 57 ms plus tard : seule la réponse est perdue,
// et le coach voit un échec. Ce PATCH n'est pas rejouable (une ligne par appel) :
// on regarde plutôt si une ligne de même durée est apparue depuis l'envoi.
// `knownIds` = ids présents avant l'envoi (null : pas de point de comparaison).
// La durée écarte les autres vidéos du même lot, envoyées en parallèle.
export async function coachFeedbackLanded(listRows, knownIds, duration, {
  attempts = 3,
  delayMs = 2000,
  doc,
  sleep = defaultSleep,
} = {}) {
  if (!knownIds) return false;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    // Attente d'abord : la requête peut encore être en cours côté serveur.
    await sleep(delayMs * attempt);
    await waitUntilVisible(doc);
    try {
      const rows = await listRows();
      if (rows.some((r) => !knownIds.includes(r.id)
        && (duration == null || Math.round(r.duration) === Math.round(duration)))) return true;
    } catch { /* réseau encore coupé : nouvel essai */ }
  }
  return false;
}

/**
 * @param {() => Promise<any>} send  effectue UNE tentative (relit son jeton à chaque appel)
 * @param {{ attempts?: number, baseDelayMs?: number, doc?: Document, sleep?: (ms: number) => Promise<void>, onRetry?: (err: unknown, attempt: number) => void, isCancelled?: () => boolean }} [opts]
 */
export async function confirmWithRetry(send, {
  attempts = 3,
  baseDelayMs = 1500,
  doc,
  sleep = defaultSleep,
  onRetry,
  isCancelled = () => false,
} = {}) {
  // Annulation (ex. l'élève annule l'envoi ou choisit une autre vidéo) : on
  // arrête de réessayer, l'appelant ignore cette erreur (cancelled: true).
  const cancelledError = () => Object.assign(new Error('Upload cancelled'), { cancelled: true });
  let lastErr;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    await waitUntilVisible(doc);
    if (isCancelled()) throw cancelledError();
    try {
      return await send();
    } catch (err) {
      lastErr = err;
      if (!isRetryableConfirmError(err) || attempt === attempts - 1) throw err;
      if (isCancelled()) throw cancelledError();
      onRetry?.(err, attempt + 1);
      await sleep(baseDelayMs * 2 ** attempt);
    }
  }
  throw lastErr;
}
